import { useRef, useState, useEffect } from "react";
import { Camera, Loader2, X, Check, Sparkles, Barcode, ScanLine, Upload, Repeat } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MEALS = [
  { v: "breakfast", l: "Breakfast" },
  { v: "lunch", l: "Lunch" },
  { v: "dinner", l: "Dinner" },
  { v: "snack", l: "Snack" },
];

const KIND_ICON = {
  food: Sparkles,
  product: Sparkles,
  label: ScanLine,
  barcode: Barcode,
  unknown: Camera,
};

const KIND_LABEL = {
  food: "FOOD DETECTED",
  product: "PRODUCT DETECTED",
  label: "NUTRITION LABEL",
  barcode: "BARCODE",
  unknown: "UNKNOWN",
};

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1280;
        let { width, height } = img;
        if (width > max || height > max) {
          const ratio = max / Math.max(width, height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function captureVideoFrame(videoEl) {
  const max = 1280;
  let w = videoEl.videoWidth;
  let h = videoEl.videoHeight;
  if (w > max || h > max) {
    const r = max / Math.max(w, h);
    w = Math.round(w * r);
    h = Math.round(h * r);
  }
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(videoEl, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", 0.85);
}

export default function SnapToLog({ onClose, onSaved, defaultMeal = "snack" }) {
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [mode, setMode] = useState("choose"); // choose | camera | preview
  const [facingMode, setFacingMode] = useState("environment");
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [imgDataUri, setImgDataUri] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [meal, setMeal] = useState(defaultMeal);

  // Cleanup the camera stream on unmount or mode change
  useEffect(() => {
    return () => stopStream();
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  };

  const startCamera = async (preferredFacing = facingMode) => {
    setCameraError(null);
    setCameraReady(false);
    stopStream();

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError("Your browser doesn't support camera access. Use Upload instead.");
      return;
    }

    // Detect if we're inside an iframe — many preview iframes don't grant camera permission
    const inIframe = (() => { try { return window.top !== window.self; } catch (_) { return true; } })();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: preferredFacing },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setMode("camera");
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            setCameraReady(true);
            videoRef.current.play().catch(() => {});
          };
        }
      }, 50);
    } catch (err) {
      const name = err?.name || "Error";
      let msg;
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        msg = inIframe
          ? "Camera blocked inside this preview frame. Open the site in a new tab (top-right ↗) and try again — your browser will prompt for camera permission there."
          : "Camera permission denied. Allow camera access in your browser address bar, then click Open Camera again.";
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        msg = "No camera found on this device. Use Upload instead.";
      } else if (name === "NotReadableError") {
        msg = "Your camera is busy in another app. Close it and try again.";
      } else if (name === "OverconstrainedError") {
        msg = "Your camera doesn't support that mode. Use Upload instead.";
      } else {
        msg = `Camera error: ${err?.message || name}. Use Upload instead.`;
      }
      setCameraError(msg);
      setMode("choose");
    }
  };

  const flipCamera = async () => {
    const next = facingMode === "environment" ? "user" : "environment";
    setFacingMode(next);
    await startCamera(next);
  };

  const snapFromCamera = async () => {
    if (!videoRef.current || !cameraReady) return;
    const dataUri = captureVideoFrame(videoRef.current);
    stopStream();
    setImgDataUri(dataUri);
    setMode("preview");
    await analyzeImage(dataUri);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|jpg)$/i.test(file.type)) {
      toast.error("Use a JPEG, PNG, or WEBP photo.");
      return;
    }
    try {
      const dataUri = await fileToBase64(file);
      setImgDataUri(dataUri);
      setMode("preview");
      await analyzeImage(dataUri);
    } catch (err) {
      toast.error("Couldn't read that file.");
      setImgDataUri(null);
      setMode("choose");
    }
  };

  const analyzeImage = async (dataUri) => {
    setAnalyzing(true);
    setParsed(null);
    try {
      const { data } = await api.post("/nutrition/snap", {
        image_base64: dataUri.split(",")[1],
      });
      setParsed({
        ...data,
        calories: Number(data.calories || 0),
        protein_g: Number(data.protein_g || 0),
        carbs_g: Number(data.carbs_g || 0),
        fat_g: Number(data.fat_g || 0),
        name: data.name || "Item",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI couldn't read that photo. Try a clearer shot.");
      setImgDataUri(null);
      setMode("choose");
    } finally {
      setAnalyzing(false);
    }
  };

  const reset = () => {
    stopStream();
    setImgDataUri(null);
    setParsed(null);
    setMode("choose");
    setCameraError(null);
  };

  const save = async () => {
    if (!parsed) return;
    setSaving(true);
    try {
      await api.post("/nutrition/log", {
        name: parsed.name,
        calories: parsed.calories,
        protein_g: parsed.protein_g,
        carbs_g: parsed.carbs_g,
        fat_g: parsed.fat_g,
        meal,
      });
      toast.success("Logged.");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    stopStream();
    onClose?.();
  };

  const Icon = parsed ? KIND_ICON[parsed.kind] || Camera : Camera;

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4" data-testid="snap-modal">
      <div className="bg-[#0a0a0a] brutal-border w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-[#FF3333]" />
            <div className="font-heading uppercase text-xl tracking-wide">Snap to Log</div>
          </div>
          <button onClick={closeModal} data-testid="snap-close" className="text-white/40 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* CHOOSE: pick camera or upload */}
          {mode === "choose" && (
            <div>
              <p className="font-sub text-white/60 text-sm">
                Snap a photo of your food, a product label, or a barcode. FORGE-AI estimates the macros and grows smarter from every log.
              </p>

              {cameraError && (
                <div className="brutal-border-red bg-[#FF3333]/10 p-3 mt-4 font-mono text-[11px] text-[#FF3333]" data-testid="snap-camera-error">
                  {cameraError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 mt-5">
                <button
                  onClick={() => startCamera()}
                  data-testid="snap-open-camera"
                  className="brutal-border-red bg-black/60 p-6 flex flex-col items-center gap-2 hover:bg-[#FF3333]/5 transition-colors"
                >
                  <Camera className="w-8 h-8 text-[#FF3333]" />
                  <div className="font-heading uppercase text-sm tracking-wider">Open Camera</div>
                  <div className="font-mono text-[9px] text-white/40 tracking-widest">LIVE PREVIEW</div>
                </button>
                <button
                  onClick={() => fileRef.current?.click()}
                  data-testid="snap-upload"
                  className="brutal-border-blue bg-black/60 p-6 flex flex-col items-center gap-2 hover:bg-[#00E5FF]/5 transition-colors"
                >
                  <Upload className="w-8 h-8 text-[#00E5FF]" />
                  <div className="font-heading uppercase text-sm tracking-wider">Upload</div>
                  <div className="font-mono text-[9px] text-white/40 tracking-widest">FILE / PHOTO ROLL</div>
                </button>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFile}
                data-testid="snap-file-input"
                className="hidden"
              />
              <div className="mt-4 grid grid-cols-3 gap-2 font-mono text-[10px] text-white/40 tracking-widest text-center">
                <div className="border border-white/10 p-2"><Sparkles className="w-3 h-3 mx-auto mb-1 text-[#FF3333]" />FOOD</div>
                <div className="border border-white/10 p-2"><ScanLine className="w-3 h-3 mx-auto mb-1 text-[#00E5FF]" />LABEL</div>
                <div className="border border-white/10 p-2"><Barcode className="w-3 h-3 mx-auto mb-1 text-[#FFB800]" />BARCODE</div>
              </div>
            </div>
          )}

          {/* CAMERA: live video preview */}
          {mode === "camera" && (
            <div data-testid="snap-camera-view">
              <div className="relative bg-black border border-[#FF3333]/30 aspect-[4/3] overflow-hidden">
                <video
                  ref={videoRef}
                  data-testid="snap-camera-video"
                  playsInline
                  muted
                  autoPlay
                  className="w-full h-full object-cover"
                />
                {!cameraReady && (
                  <div className="absolute inset-0 flex items-center justify-center flex-col gap-3 bg-black/80">
                    <Loader2 className="w-8 h-8 animate-spin text-[#FF3333]" />
                    <div className="font-mono text-[10px] tracking-[0.3em] text-white/60">STARTING CAMERA</div>
                  </div>
                )}
                {/* center reticle */}
                {cameraReady && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="w-3/5 h-3/5 border-2 border-[#FF3333]/40 border-dashed" />
                  </div>
                )}
                <div className="absolute top-3 left-3 font-mono text-[10px] tracking-widest text-[#FF3333] bg-black/60 px-2 py-1">
                  LIVE
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={reset}
                  data-testid="snap-cancel-camera"
                  className="btn-forged-ghost text-xs px-4"
                >
                  Cancel
                </button>
                <button
                  onClick={flipCamera}
                  disabled={!cameraReady}
                  data-testid="snap-flip-camera"
                  className="btn-forged-ghost text-xs px-4 disabled:opacity-50"
                  title="Flip camera"
                >
                  <Repeat className="w-3.5 h-3.5" />
                  Flip
                </button>
                <button
                  onClick={snapFromCamera}
                  disabled={!cameraReady}
                  data-testid="snap-capture"
                  className="btn-forged-red ml-auto disabled:opacity-50"
                >
                  <Camera className="w-4 h-4" /> Capture
                </button>
              </div>
              <div className="font-mono text-[10px] text-white/40 tracking-widest mt-3 text-center">
                AIM AT FOOD · BARCODE · OR LABEL
              </div>
            </div>
          )}

          {/* PREVIEW + RESULT */}
          {mode === "preview" && imgDataUri && (
            <div className="space-y-4">
              <div className="relative">
                <img src={imgDataUri} alt="snap" className="w-full max-h-72 object-contain border border-white/10 bg-black" />
                {analyzing && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center flex-col gap-3">
                    <Loader2 className="w-8 h-8 animate-spin text-[#FF3333]" />
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#FF3333]">FORGE-AI ANALYZING</div>
                  </div>
                )}
              </div>

              {!analyzing && parsed && (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon className="w-4 h-4 text-[#00E5FF]" />
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#00E5FF]">{KIND_LABEL[parsed.kind] || "DETECTED"}</div>
                    {parsed.source === "openfoodfacts" && (
                      <span className="font-mono text-[9px] text-[#FFB800] tracking-widest">· OPENFOODFACTS</span>
                    )}
                    <span className="ml-auto font-mono text-[9px] text-white/40 tracking-widest">CONF: {(parsed.confidence || "").toUpperCase()}</span>
                  </div>

                  <input
                    type="text"
                    value={parsed.name}
                    onChange={(e) => setParsed({ ...parsed, name: e.target.value })}
                    data-testid="snap-name"
                    className="w-full bg-transparent border border-white/15 focus:border-[#FF3333] outline-none px-3 py-2 font-sub text-base"
                  />

                  <div className="grid grid-cols-4 gap-2">
                    {[
                      ["calories", "KCAL", "#FF3333"],
                      ["protein_g", "PROTEIN g", "#00E5FF"],
                      ["carbs_g", "CARBS g", "#FFB800"],
                      ["fat_g", "FAT g", "#9CA3AF"],
                    ].map(([k, l, c]) => (
                      <div key={k}>
                        <div className="font-mono text-[9px] tracking-widest" style={{ color: c }}>{l}</div>
                        <input
                          type="number"
                          step="0.1"
                          value={parsed[k]}
                          onChange={(e) => setParsed({ ...parsed, [k]: parseFloat(e.target.value) || 0 })}
                          data-testid={`snap-${k}`}
                          className="w-full bg-transparent border border-white/15 focus:border-[#FF3333] outline-none px-2 py-1.5 font-heading text-xl mt-1"
                        />
                      </div>
                    ))}
                  </div>

                  {parsed.note && (
                    <p className="font-mono text-[10px] text-white/40 leading-relaxed">{parsed.note}</p>
                  )}

                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={meal}
                      onChange={(e) => setMeal(e.target.value)}
                      data-testid="snap-meal"
                      className="bg-black border border-white/15 px-3 py-2 font-sub text-sm focus:border-[#FF3333] outline-none"
                    >
                      {MEALS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                    <button
                      onClick={reset}
                      data-testid="snap-retake"
                      className="btn-forged-ghost text-xs px-4"
                    >
                      Retake
                    </button>
                    <button
                      onClick={save}
                      disabled={saving}
                      data-testid="snap-save"
                      className="btn-forged-red text-xs px-4 ml-auto disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3" /> Log it</>}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
